import { Select } from 'antd';
import React from 'react';
import { LINEUP_LIMIT } from '../../../../config/settings';
import { DropdownOption } from '../../../../core/models/DropdownOption';
import { filterOptionForSearch } from '../../../../helpers/normalizeForSearch';
import { CreateMatchPlayerCommandOfMatchPlayer } from '../../../../utils/api';

interface RosterTeamEditorProps {
    teamName: string;
    side: 'home' | 'away';
    roster: DropdownOption[];
    lineup: CreateMatchPlayerCommandOfMatchPlayer[];
    /** Slot numbers (1..LINEUP_LIMIT) currently flagged as goalkeepers. */
    goalkeepers: number[];
    onSelect: (cap: number, playerId: number) => void;
    onClear: (cap: number) => void;
    onToggleGk: (cap: number) => void;
    isSlotLocked: (cap: number) => boolean;
}

const RosterTeamEditor: React.FC<RosterTeamEditorProps> = ({
    teamName,
    side,
    roster,
    lineup,
    goalkeepers,
    onSelect,
    onClear,
    onToggleGk,
    isSlotLocked,
}) => {
    const rows = [];
    for (let cap = 1; cap <= LINEUP_LIMIT; cap += 1) {
        const lineupRequest = lineup.find((l) => l.number === cap);
        const isGoalkeeper = goalkeepers.includes(cap);

        // Hide players already placed in another slot, but keep the current one.
        const available = roster.filter(
            (p) =>
                !lineup.some((l) => l.playerId.toString() === p.id) ||
                (lineupRequest && lineupRequest.playerId.toString() === p.id)
        );

        rows.push(
            <div key={cap} className="m-slot">
                <button
                    type="button"
                    className={`m-slot__cap${isGoalkeeper ? ' m-slot__cap--gk' : ''}`}
                    onClick={lineupRequest ? (): void => onToggleGk(cap) : undefined}
                    title={lineupRequest ? 'Tap to toggle goalkeeper' : undefined}
                >
                    {lineupRequest?.capNumber || cap}
                </button>
                <div className="m-slot__select">
                    {lineupRequest && isSlotLocked(cap) ? (
                        <button
                            type="button"
                            className="m-slot__locked"
                            onClick={(): void => onClear(cap)}
                        >
                            {available.find((p) => parseInt(p.id, 10) === lineupRequest.playerId)
                                ?.name ||
                                roster.find((p) => parseInt(p.id, 10) === lineupRequest.playerId)
                                    ?.name ||
                                'Player'}
                        </button>
                    ) : (
                        <Select
                            showSearch
                            allowClear
                            style={{ width: '100%' }}
                            placeholder="Select player"
                            optionFilterProp="label"
                            value={lineupRequest ? lineupRequest.playerId : undefined}
                            filterOption={filterOptionForSearch}
                            onSelect={(value): void => onSelect(cap, value as number)}
                            onChange={(value): void => {
                                if (!value) onClear(cap);
                            }}
                            options={available.map((p) => ({
                                label: p.name,
                                value: parseInt(p.id, 10),
                            }))}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="m-roster">
            <div className={`m-roster__name m-roster__name--${side}`}>{teamName}</div>
            <div className="m-roster__hint-row">Tap a cap number to mark/unmark a goalkeeper.</div>
            {rows}
        </div>
    );
};

export default RosterTeamEditor;
